/**
 * connectSyncDO — open a y-partyserver WebSocket connection to apps/sync
 * for a given doc key and wire provider status events to a SaveState callback.
 *
 * Used by the /app route to bind the Workspace's `syncState` prop to live
 * y-partyserver connection state.
 *
 * Architecture refs:
 *   architecture.md §4.4  — CRDT sync topology; one DO per y-doc key
 *   architecture.md §4.12 — Auth; session cookie rides on WS upgrade
 *   packages/client-core/src/sync/index.ts — createSyncClient factory
 *   .plan/implementation/2026-04-26-phase-4-handover.md §4.2
 *
 * Status mapping:
 *   provider "connecting" → "syncing"
 *   provider "connected"  → "saved"
 *   provider "disconnected" → "offline"
 *   provider conflict-shaped custom message → "conflict"
 *
 * Post-sync seed trigger (2026-05-03):
 *   On the first `sync(true)` event for a `draft:<branch-uuid>` connection, if
 *   the doc's "blocks" map is empty we emit `__YPS:{ "type": "re-hydrate" }`
 *   to ask the sync DO to seed the y-doc from the branch's head_commit_uuid →
 *   attempts.body_markdown. This covers the "freshly created spin-off /
 *   variation" case where the server has written a non-empty body_markdown
 *   onto the head attempt but no y-doc has been opened yet. The server
 *   handler is idempotent (no-op if blocks map is non-empty) and treats "no
 *   head attempt" as a quiet ack, so this trigger is safe to send on every
 *   first sync — there is no failure mode where it harms a normal draft.
 *   See apps/sync/src/commit/draft-lifecycle.ts for the server side.
 *
 * The returned destroy function tears down the WS and should be called in
 * the React effect cleanup.
 */

import { createSyncClient } from "@proof/client-core/sync";
import type { YDocKey } from "@proof/client-core/sync";
import { createWebYjsStorageAdapter } from "@proof/client-core/storage/web";
import type * as Y from "yjs";
// Dark perf cache #3 evidence hooks — `?perf=1` marks for the y-doc IDB
// hydrate → server-reconcile window. No-op + tree-shaken outside dev / the
// opt-in perf profile (see lib/perf.ts PERF_ENABLED).
import { mark as perfMark, measure as perfMeasure, RECIPE_OPEN_MARKS } from "../lib/perf.js";

// ── Module-level shared adapter ─────────────────────────────────────────────
// One adapter per page-load; all doc connections share the same IDB database
// (`proof-yjs`). Lazy-initialised on first `connectSyncDO` call.
let _sharedAdapter: ReturnType<typeof createWebYjsStorageAdapter> | null = null;

function getSharedAdapter(): ReturnType<typeof createWebYjsStorageAdapter> {
  if (_sharedAdapter === null) {
    _sharedAdapter = createWebYjsStorageAdapter();
  }
  return _sharedAdapter;
}

export type { YDocKey };

/**
 * The subset of SaveState values this module emits.
 * Matches the Workspace `syncState` prop (excludes "saving" which is derived
 * from `attemptInFlight` in the Workspace itself).
 */
export type SyncState = "saved" | "syncing" | "conflict" | "offline";

export interface SyncConnection {
  /**
   * The live Y.Doc for this connection.
   *
   * Pass this to TipTapEditor's `ydoc` prop to activate the BlocksBinding
   * and make the editor CRDT-backed. The doc is owned by the connection —
   * do not call doc.destroy() directly; call destroy() on this connection.
   *
   * Architecture ref: arch review §5 Fix #1.1 — surface doc from connectSyncDO.
   */
  doc: Y.Doc;
  /**
   * Freeze the current draft as the next version via the LIVE editor provider.
   *
   * empty-Share-body v2 fix: sends a `save-as-version` envelope on THIS
   * connection's provider (the editor's long-lived WS). Because the editor's
   * typed ops and this envelope travel on the same socket, the sync-DO applies
   * every pending op to its server doc BEFORE projecting it — that FIFO ordering
   * is the barrier that makes the frozen version capture the authored content.
   *
   * Resolves with the new vN+2 draft attempt UUID + branch on success; REJECTS
   * (never silently resolves — Rule 8) on a server error / timeout / closed WS.
   */
  sendSaveAsVersion(): Promise<{
    newAttemptUuid: string;
    branchUuid: string;
    /**
     * UUID of the just-frozen version (the vN+1 record). Optional so the client
     * tolerates a not-yet-redeployed server; when present the post-freeze edit
     * prompt amends this version rather than a stale client-held draft uuid
     * (NEW-BUG-1).
     */
    frozenAttemptUuid?: string;
  }>;
  /** Tear down the WS provider and release resources. */
  destroy(): void;
}

/**
 * Open a live WS connection to the sync DO for `docKey`.
 *
 * @param docKey        The y-doc key (e.g. "draft:<branchUuid>").
 * @param syncBaseUrl   The base URL of the sync Worker (e.g. "" for same-origin).
 * @param onStateChange Called whenever the effective SaveState changes.
 * @param onHydrated    Optional. Called exactly once, on the FIRST `sync(true)`
 *                      handshake for this connection (i.e. `provider.synced`
 *                      flips true and the server's syncStep2 has been applied).
 *                      This is the real hydration-complete signal, distinct from
 *                      `onStateChange("saved")` which also fires on the earlier
 *                      "connected" status. The BodySnapshot fast-paint overlay
 *                      (Phase 1.C) uses it to dissolve on a synced-but-empty body
 *                      where no content-derived signal ever fires.
 *
 * @returns SyncConnection with a `destroy()` method for cleanup.
 *
 * Note: Session authentication is handled automatically — the browser attaches
 * the session cookie to WS upgrade requests on the same origin. No token param
 * is needed at MVP.
 */
export function connectSyncDO(
  docKey: YDocKey,
  syncBaseUrl: string,
  onStateChange: (state: SyncState) => void,
  onHydrated?: () => void,
): SyncConnection {
  // Wire the shared web storage adapter so local IDB persistence is active.
  // createSyncClient will:
  //   1. Create the provider with connect:false
  //   2. Hydrate the doc from IDB (via Y.applyUpdate — spike 0.C safe)
  //   3. Attach the update persistence listener
  //   4. Then call provider.connect() to open the WS
  // The doc therefore has locally-cached content before the WS round-trip.
  const client = createSyncClient(docKey, {
    baseUrl: syncBaseUrl,
    syncPath: "/sync",
    connect: true,
    storageAdapter: getSharedAdapter(),
  });

  const { provider, doc } = client;

  // ── Perf mark: y-doc IDB cache hydrate (dark cache #3, warm-open evidence) ──
  // Resolves before the WS handshake. A count > 0 means the authoritative live
  // doc was seeded from local IDB, not from a blocking server round-trip. This
  // stamps the START of the cache→reconcile window measured on `yjsReconciled`.
  void client.hydrated
    .then((count) => {
      if (count > 0) perfMark(RECIPE_OPEN_MARKS.yjsCacheHydrate);
    })
    // eslint-disable-next-line no-restricted-syntax -- best-effort perf telemetry, not a user-data mutation; hydration failures are already logged inside createSyncClient (yjs_hydrate_fatal). Rule 8 documented degradation: fail open, no double-log.
    .catch(() => {});

  // ── Post-sync re-hydrate trigger (draft:* only) ─────────────────────────────
  // Once-per-connection guard so reconnects (e.g. brief offline) do not re-fire
  // the trigger. The server handler is idempotent on doc content, but skipping
  // the round-trip when we already know the doc has content is cheaper.
  let rehydrateTried = false;
  const isDraftKey = typeof docKey === "string" && docKey.startsWith("draft:");

  const maybeRequestRehydrate = () => {
    if (rehydrateTried) return;
    if (!isDraftKey) return;
    if (!shouldRequestRehydrate(doc)) return;
    rehydrateTried = true;
    sendRehydrateMessage(provider);
  };

  // ── Status event → SaveState ────────────────────────────────────────────────
  // y-partyserver/provider emits { status: "connected" | "disconnected" | ... }

  const onStatus = (event: { status: string }) => {
    switch (event.status) {
      case "connected":
        onStateChange("saved");
        break;
      case "disconnected":
        onStateChange("offline");
        break;
      case "connecting":
        onStateChange("syncing");
        break;
      default:
        // Unknown status — don't change state.
        break;
    }
  };

  // ── Sync start → syncing ────────────────────────────────────────────────────
  // y-partyserver fires "sync" once the initial doc state arrives.
  // Before sync arrives, we treat the connection as "syncing".
  let hydratedFired = false;
  const onSync = (synced: boolean) => {
    if (synced) {
      onStateChange("saved");
      // The initial sync handshake completed — at this point R2-loaded state
      // (if any) has been applied to `doc`. Fire the hydration-complete signal
      // exactly once (reconnects re-emit sync(true) but the body is already
      // hydrated). This is the terminal dissolve signal for the fast-paint
      // overlay's synced-but-empty case (Phase 1.C).
      if (!hydratedFired) {
        hydratedFired = true;
        // Perf mark: the IDB-hydrated (possibly stale) local doc has now
        // reconciled with the server's authoritative state. Closes the
        // cache→reconcile exposure window opened at `yjsCacheHydrate`.
        perfMark(RECIPE_OPEN_MARKS.yjsReconciled);
        perfMeasure(
          "recipe.yjsReconcileWindow",
          RECIPE_OPEN_MARKS.yjsCacheHydrate,
          RECIPE_OPEN_MARKS.yjsReconciled,
        );
        onHydrated?.();
      }
      // If the blocks map is still empty and this is a draft key, ask the server
      // to seed from head attempt.
      maybeRequestRehydrate();
    } else {
      onStateChange("syncing");
    }
  };

  // ── Custom messages → conflict detection ───────────────────────────────────
  // The sync DO may broadcast a custom-message shaped like:
  //   { type: "conflict", ... }
  // Wire this to the "conflict" SaveState so the Workspace TopBar can show
  // the conflict indicator and onConflictClick handler.

  const onCustomMessage = (msg: string) => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(msg);
    } catch {
      return;
    }
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      (parsed as { type?: unknown }).type === "conflict"
    ) {
      onStateChange("conflict");
    }
  };

  provider.on("status", onStatus);
  provider.on("sync", onSync);
  provider.on("custom-message", onCustomMessage);

  // Fire initial "syncing" so the Workspace reflects the connecting state.
  onStateChange("syncing");

  return {
    doc: client.doc,
    sendSaveAsVersion() {
      return sendSaveAsVersionOnProvider(provider as unknown as SaveAsVersionProvider);
    },
    destroy() {
      provider.off("status", onStatus);
      provider.off("sync", onSync);
      provider.off("custom-message", onCustomMessage);
      client.destroy();
    },
  };
}

// ── Yjs cache purge (sign-out path) ─────────────────────────────────────────

/**
 * Purge every Yjs doc cached in IDB for the current user's session.
 *
 * Called in `handleSignOut` (apps/web/src/routes/app.tsx) alongside
 * `invalidateAll(userId)` so the next user on this device cannot see the
 * previous user's draft content.
 *
 * Best-effort: a purge failure must not block sign-out (callers should
 * wrap in try/catch and log the error).
 */
export async function purgeWebYjsCache(): Promise<void> {
  const { purgeYjsCache } = await import("@proof/client-core");
  await purgeYjsCache(getSharedAdapter());
}

// ── Save-as-version freeze over the LIVE provider (empty-Share-body v2) ────────

/** Wall-clock timeout for the save-as-version freeze round-trip (ms). Mirrors
 *  the commit transport's DEFAULT_COMMIT_TIMEOUT_MS in remote-backend.ts. */
const SAVE_AS_VERSION_TIMEOUT_MS = 15_000;

/**
 * Provider surface used by `sendSaveAsVersionOnProvider` — narrowed so tests can
 * pass a minimal stub.
 */
export interface SaveAsVersionProvider {
  ws: WebSocket | null | undefined;
  /**
   * y-partyserver sync-handshake state. `false` on every (re)connect, `true`
   * only after the client has processed the server's syncStep2. Gating the
   * freeze envelope on this (not merely on `status: connected`) is what closes
   * the reconnect gap — see `trySend` below.
   */
  synced?: boolean | undefined;
  on: (event: string, handler: (e: unknown) => void) => void;
  off: (event: string, handler: (e: unknown) => void) => void;
}

/**
 * Send a `save-as-version` envelope on an already-open provider and await the
 * server ack. The envelope rides the SAME WS as the editor's typed ops, so the
 * sync-DO has applied every pending op to its server doc before it projects +
 * freezes (the FIFO barrier).
 *
 * Reconnect gap (why we gate on `synced`, not `status: connected`): the
 * `status` event flips to "connected" the moment the socket opens — BEFORE the
 * client has flushed the edits it made while offline to the server (that flush
 * is the syncStep1/syncStep2 handshake). Firing the freeze on "connected"
 * mid-reconnect can therefore project a server doc that is missing those edits.
 * `provider.synced` is reset to false on every (re)connect and set true only
 * after the client processes the server's syncStep2 — the point at which the
 * docs have reconciled — and the provider emits a `sync` event when it flips.
 * We send only once `synced === true`, waiting on the `sync` event otherwise.
 *
 * Resolves with `{ newAttemptUuid, branchUuid }`; rejects (Rule 8 — never
 * silently resolves) on `save-as-version-error`, timeout, or socket close.
 *
 * Exported for unit testing.
 */
export function sendSaveAsVersionOnProvider(
  provider: SaveAsVersionProvider,
): Promise<{ newAttemptUuid: string; branchUuid: string; frozenAttemptUuid?: string }> {
  return new Promise((resolve, reject) => {
    let settled = false;
    const payload = `__YPS:${JSON.stringify({ type: "save-as-version" })}`;

    let sent = false;

    const cleanup = () => {
      settled = true;
      provider.off("custom-message", onCustomMessage as (e: unknown) => void);
      provider.off("status", onStatus);
      provider.off("sync", onSync);
      clearTimeout(timer);
    };

    const onCustomMessage = (msg: unknown) => {
      if (settled || typeof msg !== "string") return;
      let parsed: unknown;
      try {
        parsed = JSON.parse(msg);
      } catch {
        return; // not a JSON envelope; keep listening
      }
      if (
        typeof parsed !== "object" ||
        parsed === null ||
        typeof (parsed as { type?: unknown }).type !== "string"
      ) {
        return;
      }
      const type = (parsed as { type: string }).type;
      if (type === "save-as-version-ack") {
        const ack = parsed as {
          newAttemptUuid?: unknown;
          branchUuid?: unknown;
          frozenAttemptUuid?: unknown;
        };
        if (typeof ack.newAttemptUuid === "string" && typeof ack.branchUuid === "string") {
          cleanup();
          // NEW-BUG-1: carry the frozen version's uuid through when present so
          // the post-freeze prompt amends the correct version. Tolerant of a
          // legacy server ack that omits it (falls back to client state).
          resolve({
            newAttemptUuid: ack.newAttemptUuid,
            branchUuid: ack.branchUuid,
            ...(typeof ack.frozenAttemptUuid === "string"
              ? { frozenAttemptUuid: ack.frozenAttemptUuid }
              : {}),
          });
        } else {
          cleanup();
          reject(new Error("save_as_version_malformed: ack missing fields"));
        }
      } else if (type === "save-as-version-error") {
        const errMsg = parsed as { reason?: unknown; message?: unknown };
        cleanup();
        reject(
          new Error(
            `save_as_version_error: ${String(errMsg.reason ?? "unknown")}${
              errMsg.message ? `: ${String(errMsg.message)}` : ""
            }`,
          ),
        );
      }
    };

    const timer = setTimeout(() => {
      if (settled) return;
      cleanup();
      reject(new Error(`save_as_version_timeout: no ack within ${SAVE_AS_VERSION_TIMEOUT_MS} ms`));
    }, SAVE_AS_VERSION_TIMEOUT_MS);

    const removeTriggers = () => {
      provider.off("status", onStatus);
      provider.off("sync", onSync);
    };

    const trySend = (): boolean => {
      if (sent) return true;
      const ws = provider.ws;
      // Gate on BOTH the socket being open AND the sync handshake being
      // complete (`synced === true`). See the function-level comment: sending
      // on "connected" alone can freeze before a reconnect's local edits reach
      // the server.
      if (ws && ws.readyState === /* OPEN */ 1 && provider.synced === true) {
        sent = true;
        removeTriggers();
        try {
          ws.send(payload);
        } catch (err) {
          if (!settled) {
            cleanup();
            reject(new Error(`save_as_version_send_failed: ${String(err)}`));
          }
        }
        return true;
      }
      return false;
    };

    // A reconnect flips status→connected before the handshake; the `sync` event
    // (fired with `true` once synced) is the reliable go-signal. We listen on
    // both so an already-synced provider that was momentarily not-OPEN still
    // sends the instant the socket reopens.
    const onStatus = (e: unknown) => {
      if (
        typeof e === "object" &&
        e !== null &&
        (e as { status?: string }).status === "connected"
      ) {
        trySend();
      }
    };

    const onSync = (e: unknown) => {
      if (e === true) trySend();
    };

    provider.on("custom-message", onCustomMessage as (e: unknown) => void);

    if (!trySend()) {
      provider.on("sync", onSync);
      provider.on("status", onStatus);
    }
  });
}

// ── Re-hydrate trigger helpers (exported for unit testing) ─────────────────────

/**
 * Decide whether a freshly-synced y-doc should be re-hydrated from the server's
 * stored body_markdown. Returns true iff the doc's "blocks" map is empty.
 *
 * Exported for unit testing. The server-side handler (handleRehydrateDraft) is
 * idempotent — calling it on a non-empty doc is a no-op — but the client-side
 * guard avoids the round-trip.
 *
 * The "blocks" map name matches the schema in
 * `apps/sync/src/commit/reparse.ts` (seedYDocFromMarkdown) and
 * `packages/grammar/src/project/canonical-markdown.ts` (projectToCanonicalMarkdown).
 */
export function shouldRequestRehydrate(doc: Y.Doc): boolean {
  const blocks = doc.getMap("blocks");
  return blocks.size === 0;
}

/**
 * Provider shape used by `sendRehydrateMessage` — narrowed to the surface we
 * actually need so tests can pass a minimal stub.
 */
export interface RehydrateProvider {
  ws: WebSocket | null | undefined;
  on: (event: string, handler: (e: unknown) => void) => void;
  off: (event: string, handler: (e: unknown) => void) => void;
}

/**
 * Send the `__YPS:{ "type": "re-hydrate" }` envelope on the provider's WS.
 *
 * If the WS is already open, sends immediately. Otherwise installs a one-shot
 * "status" listener that fires when the connection reaches OPEN. Mirrors the
 * pattern used in `packages/client-core/src/backend/remote-backend.ts` for
 * commit envelopes.
 *
 * Exported for unit testing.
 */
export function sendRehydrateMessage(provider: RehydrateProvider): void {
  const payload = `__YPS:${JSON.stringify({ type: "re-hydrate" })}`;

  const trySend = (): boolean => {
    const ws = provider.ws;
    if (ws && ws.readyState === /* OPEN */ 1) {
      try {
        ws.send(payload);
      } catch (err) {
        // Surface any send failure to the operator. Rule 8: never silently
        // swallow — but this is a server-side seeding nudge, not a user
        // mutation, so a console.error is the right shape (no toast).
        console.error({ event: "sync_rehydrate_send_failed", err: String(err) });
      }
      return true;
    }
    return false;
  };

  if (trySend()) return;

  const onStatus = (e: unknown) => {
    if (typeof e === "object" && e !== null && (e as { status?: string }).status === "connected") {
      if (trySend()) {
        provider.off("status", onStatus);
      }
    }
  };
  provider.on("status", onStatus);
}
